import { PointerField } from "@opendaw/lib-box";
import { AudioBusBox, AudioEffectCompositeCellBox, AudioUnitBox, AuxSendBox, GrooveShuffleBox, LfoModulatorBox, MacroModulatorBox, MarkerBox, MIDIOutputParameterBox, ModulationBox, NoteEventBox, PlayfieldSampleBox, RandomModulatorBox, StepsModulatorBox, TrackBox, ValueEventBox, WerkstattParameterBox, WerkstattSampleBox } from "@opendaw/studio-boxes";
import { isDefined, isNull, panic } from "@opendaw/lib-std";
import { Facade } from "./Common";
import { AudioEffectCompositeEntryImpl, AudioEffectImpls } from "./devices/AudioEffects";
import { MIDIEffectImpls } from "./devices/MIDIEffects";
import { InstrumentImpls, MIDIOutputParameterImpl, PlayfieldSlotImpl } from "./devices/Instruments";
import { ScriptParameterImpl, ScriptSampleImpl } from "./devices/ScriptDevices";
import { AudioUnitImpls } from "./AudioUnits";
import { SendImpl } from "./Sends";
import { GrooveShuffleImpl } from "./GrooveShuffleImpl";
import { ModulationImpl, ModulatorImpls } from "./Modulators";
import { TrackImpls } from "./timeline/Tracks";
import { MarkerImpl } from "./timeline/Markers";
import { NoteEventImpl, ValueEventImpl } from "./timeline/Events";
export var Facades;
(function (Facades) {
    const pointerNamed = (box, name) => box.fields().find(field => field.fieldName === name && field instanceof PointerField);
    Facades.parentBox = (box) => {
        const pointer = pointerNamed(box, "host") ?? pointerNamed(box, "device") ?? pointerNamed(box, "composite")
            ?? pointerNamed(box, "audio-unit") ?? pointerNamed(box, "owner");
        if (!isDefined(pointer)) {
            return null;
        }
        return pointer.targetVertex.mapOr(vertex => vertex.box, null);
    };
    Facades.audioUnitBoxOf = (box) => {
        let current = box;
        while (!isNull(current)) {
            if (current instanceof AudioUnitBox) {
                return current;
            }
            if (current instanceof AudioBusBox) {
                return current.output.targetVertex.mapOr(vertex => vertex.box, null);
            }
            current = Facades.parentBox(current);
        }
        return panic(`${box.name} does not belong to an audio unit`);
    };
    Facades.audioUnitOf = (context, box) => AudioUnitImpls.wrap(context, Facades.audioUnitBoxOf(box));
    Facades.forBox = (context, box) => {
        if (box instanceof AudioUnitBox) {
            return AudioUnitImpls.wrap(context, box);
        }
        if (box instanceof AudioBusBox) {
            return AudioUnitImpls.wrap(context, Facades.audioUnitBoxOf(box));
        }
        if (InstrumentImpls.isBox(box)) {
            return InstrumentImpls.wrap(context, box);
        }
        if (MIDIEffectImpls.isBox(box)) {
            return MIDIEffectImpls.wrap(context, box);
        }
        if (AudioEffectImpls.isBox(box)) {
            return AudioEffectImpls.wrap(context, box);
        }
        if (box instanceof PlayfieldSampleBox) {
            return PlayfieldSlotImpl.wrap(context, box);
        }
        if (box instanceof AudioEffectCompositeCellBox) {
            return AudioEffectCompositeEntryImpl.wrap(context, box);
        }
        if (box instanceof AuxSendBox) {
            return SendImpl.wrap(context, box);
        }
        if (box instanceof WerkstattParameterBox) {
            return ScriptParameterImpl.wrap(context, box);
        }
        if (box instanceof WerkstattSampleBox) {
            return ScriptSampleImpl.wrap(context, box);
        }
        if (box instanceof MIDIOutputParameterBox) {
            return MIDIOutputParameterImpl.wrap(context, box);
        }
        if (box instanceof GrooveShuffleBox) {
            return GrooveShuffleImpl.wrap(context, box);
        }
        if (box instanceof LfoModulatorBox || box instanceof StepsModulatorBox
            || box instanceof MacroModulatorBox || box instanceof RandomModulatorBox) {
            return ModulatorImpls.wrap(context, box);
        }
        if (box instanceof ModulationBox) {
            return ModulationImpl.wrap(context, box);
        }
        if (box instanceof TrackBox) {
            return TrackImpls.wrap(context, box);
        }
        if (box instanceof MarkerBox) {
            return MarkerImpl.wrap(context, box);
        }
        if (box instanceof NoteEventBox) {
            return NoteEventImpl.wrap(context, box);
        }
        if (box instanceof ValueEventBox) {
            return ValueEventImpl.wrap(context, box);
        }
        return null;
    };
    Facades.forVertex = (context, vertex) => {
        if (vertex.isBox()) {
            return Facades.forBox(context, vertex);
        }
        return Facades.forBox(context, vertex.box);
    };
    Facades.boxOf = (facade) => {
        if (facade instanceof Facade) {
            return facade.box;
        }
        return panic(new TypeError(`Expected an openDAW object, got ${describe(facade)}`));
    };
    Facades.isCompositeBox = (box) => AudioEffectImpls.isCompositeBox(box);
    Facades.compositeInputField = (box) => box.input;
    Facades.isNestedIn = (owner, composite) => {
        let current = Facades.parentBox(owner);
        while (!isNull(current)) {
            if (current === composite) {
                return true;
            }
            if (current instanceof AudioUnitBox) {
                return false;
            }
            current = Facades.parentBox(current);
        }
        return false;
    };
    const describe = (value) => {
        if (isNull(value)) {
            return "null";
        }
        if (!isDefined(value)) {
            return "undefined";
        }
        return typeof value === "object" ? value.constructor.name : typeof value;
    };
})(Facades || (Facades = {}));
